import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { translations, type AppLanguage } from './translations';
import { LocaleContext } from './useTranslation';

const STORAGE_KEY = 'monday-gis-language';

function readStoredLanguage(): AppLanguage {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === 'he' || raw === 'en' || raw === 'ar' || raw === 'ru') return raw;
  } catch {
    /* ignore */
  }
  return 'he';
}

type LocaleContextValue = {
  language: AppLanguage;
  setLanguage: (lang: AppLanguage) => void;
};

const LanguageContext = createContext<LocaleContextValue | null>(null);

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useLocale must be used within LocaleProvider');
  }
  return ctx;
}

/** Overrides locale for print/PDF routes (`?lang=`). */
export function ForcedLocaleShell({
  language,
  children,
}: {
  language: AppLanguage;
  children: ReactNode;
}) {
  const t = useMemo(() => translations[language], [language]);
  const dir = language === 'he' || language === 'ar' ? 'rtl' : 'ltr';

  useEffect(() => {
    document.documentElement.dir = dir;
    document.documentElement.lang = language;
  }, [dir, language]);

  return (
    <LocaleContext.Provider value={t}>
      <div dir={dir} lang={language} style={{ minHeight: '100vh' }}>
        {children}
      </div>
    </LocaleContext.Provider>
  );
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<AppLanguage>(readStoredLanguage);

  const setLanguage = (lang: AppLanguage) => {
    setLanguageState(lang);
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      /* ignore */
    }
  };

  const t = useMemo(() => translations[language], [language]);

  const dir = language === 'he' || language === 'ar' ? 'rtl' : 'ltr';
  const lang = language;

  useEffect(() => {
    document.documentElement.dir = dir;
    document.documentElement.lang = lang;
  }, [dir, lang]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
      <LocaleContext.Provider value={t}>
        <div dir={dir} lang={lang} style={{ minHeight: '100vh' }}>
          {children}
        </div>
      </LocaleContext.Provider>
    </LanguageContext.Provider>
  );
}
